import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const initialForm = { email: "", password: "" };

export function AuthCard({ user, loading, onLogin, onLogout, onRegister }) {
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const title = useMemo(() => {
    if (user) {
      return "Passage rattaché à cette adresse.";
    }
    return mode === "register"
      ? "Attacher le passage à une adresse sobre."
      : "Reprendre un passage déjà ouvert.";
  }, [mode, user]);

  const handleChange = ({ target }) => {
    setForm((current) => ({ ...current, [target.name]: target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setErrorMessage("");

    try {
      if (mode === "register") {
        await onRegister(form);
      } else {
        await onLogin(form);
      }
      setForm(initialForm);
    } catch (error) {
      setErrorMessage(error.message || "Le seuil de compte reste silencieux pour l’instant.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="lumen-card value-fragment access-auth-card" data-testid="access-auth-card">
      <div className="access-auth-shell">
        <div className="space-y-4">
          <div className="lumen-panel-topline" data-testid="access-auth-topline">
            <p className="lumen-panel-kicker" data-testid="access-auth-kicker">
              Compte de passage
            </p>
            <span className="lumen-plus">+</span>
          </div>
          <p className="access-auth-intro" data-testid="access-auth-title">
            {title}
          </p>
        </div>

        {loading ? (
          <p className="lumen-copy" data-testid="access-auth-loading">
            Le compte se vérifie dans le silence.
          </p>
        ) : user ? (
          <div className="space-y-5" data-testid="access-auth-user-panel">
            <div className="access-account-summary" data-testid="access-auth-user-summary">
              <p className="lumen-panel-kicker" data-testid="access-auth-user-email-label">
                Adresse liée
              </p>
              <p className="text-lg leading-relaxed text-[var(--lumen-ivory)]" data-testid="access-auth-user-email">
                {user.email}
              </p>
            </div>

            <Button
              className="lumen-outline-button w-full"
              data-testid="access-auth-logout-button"
              onClick={onLogout}
              type="button"
              variant="outline"
            >
              Fermer ce passage
            </Button>
          </div>
        ) : (
          <form className="space-y-5" onSubmit={handleSubmit} data-testid="access-auth-form">
            <div className="access-auth-mode-row" data-testid="access-auth-mode-row">
              <Button
                className={mode === "register" ? "access-auth-mode-button access-auth-mode-button--active flex-1" : "access-auth-mode-button flex-1"}
                data-testid="access-auth-register-tab"
                onClick={() => setMode("register")}
                type="button"
                variant="ghost"
              >
                Ouvrir un compte
              </Button>
              <Button
                className={mode === "login" ? "access-auth-mode-button access-auth-mode-button--active flex-1" : "access-auth-mode-button flex-1"}
                data-testid="access-auth-login-tab"
                onClick={() => setMode("login")}
                type="button"
                variant="ghost"
              >
                Reprendre l’accès
              </Button>
            </div>

            <label className="space-y-2" data-testid="access-auth-email-wrapper">
              <span className="lumen-field-label" data-testid="access-auth-email-label">
                Adresse
              </span>
              <Input
                className="lumen-input threshold-input"
                data-testid="access-auth-email-input"
                name="email"
                onChange={handleChange}
                placeholder="adresse@domaine.com"
                required
                type="email"
                value={form.email}
              />
            </label>

            <label className="space-y-2" data-testid="access-auth-password-wrapper">
              <span className="lumen-field-label" data-testid="access-auth-password-label">
                Mot de passe
              </span>
              <Input
                className="lumen-input threshold-input"
                data-testid="access-auth-password-input"
                minLength={8}
                name="password"
                onChange={handleChange}
                placeholder="8 caractères minimum"
                required
                type="password"
                value={form.password}
              />
            </label>

            {errorMessage ? (
              <p className="threshold-status-message is-error" data-testid="access-auth-error">
                {errorMessage}
              </p>
            ) : null}

            <Button className="lumen-gold-button w-full" data-testid="access-auth-submit-button" disabled={submitting} type="submit">
              {submitting
                ? "Le passage s’ouvre..."
                : mode === "register"
                  ? "Ouvrir ce compte"
                  : "Reprendre ce passage"}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}